/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jul 23 2024
 **/
import { useEffect, useState } from "react";

import { _T } from "@rfice/language";
import { Row, TextField, Typography } from "@/frameworks/designStuff/components";

import * as S from "./private/emotion";
import { Avatar } from "@/frameworks/core/components";
import { useMyDeskActions } from "@/frameworks/core/API";
import { useKnockManager } from "@/frameworks/core/hooks";

const KNOCK_IMAGE = [
	"Frame_Sequence_Knock_00",
	"Frame_Sequence_Knock_01",
	"Frame_Sequence_Knock_02",
	"Frame_Sequence_Knock_03",
	"Frame_Sequence_Knock_04",
	"Frame_Sequence_Knock_05",
	"Frame_Sequence_Knock_06",
	"Frame_Sequence_Knock_07",
	"Frame_Sequence_Knock_08",
	"Frame_Sequence_Knock_09",
	"Frame_Sequence_Knock_10",
	"Frame_Sequence_Knock_11"
];

export default function ReceiveKnock({
	userID,
	userName,
	spaceName,
	focus,
	uniqueIndex
}: {
	userID: string;
	userName: string;
	spaceName?: string;
	focus: boolean;
	uniqueIndex: string;
}) {
	const [responseData, setResponseData] = useState("");
	const [percentageWidth, setPercentageWidth] = useState(100);
	const [frameIndex, setFrameIndex] = useState(0);
	const { knockActions } = useKnockManager();
	const frameCount = KNOCK_IMAGE.length;
	const animationDuration = 1500;
	const intervalDuration = animationDuration / frameCount;
	const hasResponseData = responseData.length > 0;

	const myDeskAPI = useMyDeskActions();

	useEffect(() => {
		const interval = setInterval(() => {
			setFrameIndex((prev) => (prev + 1) % frameCount);
		}, intervalDuration);

		return () => clearInterval(interval);
	}, [intervalDuration]);

	useEffect(() => {
		const interval = setInterval(() => {
			setPercentageWidth((prevWidth) => {
				const newWidth = prevWidth - 1;
				if (newWidth <= 0) {
					clearInterval(interval);
					return 0;
				}
				return newWidth;
			});
		}, 100);

		return () => clearInterval(interval);
	}, []);

	const close = () => {
		knockActions.hide(uniqueIndex);
	};

	const replyKnockMessage = async () => {
		await myDeskAPI.knock.reply.execute({ userId: userID, content: responseData });
		close();
	};

	useEffect(() => {
		if (percentageWidth === 0) {
			close();
		}
	}, [percentageWidth]);

	return (
		<S.Container horizonAlign="center" focus={focus}>
			<S.Sequence>
				<S.SequenceImg src={require(`assets/knockImg/${KNOCK_IMAGE[frameIndex]}.png`)} />
				<S.AvatarStyle horizonAlign="center">
					<Avatar id={userID} kind="people_64" />
				</S.AvatarStyle>
				<S.LoadingBarTotal>
					<S.LoadingBar style={{ width: `${percentageWidth}%` }} />
				</S.LoadingBarTotal>
			</S.Sequence>
			<Typography typoColor="neutral_00_100" typoSize="Head6" style={{ marginTop: "27px" }}>
				{userName}
			</Typography>
			<Typography typoColor="neutral_00_100" typoSize="Title1_B">
				{_T("{{spaceName}} 에서 나를 깨우고 있어요.", {
					spaceName: spaceName ? spaceName : _T("마이룸")
				})}
			</Typography>
			<TextField
				autoFocus
				placeholder={_T("메시지를 입력해주세요")}
				value={responseData}
				onChange={(e) => setResponseData(e.target.value)}
				style={{ marginTop: "12px", border: "none" }}
				maxLength={50}
			/>
			<Row gap={8} horizonAlign="center" style={{ marginTop: "16px" }}>
				<S.Button onClick={replyKnockMessage} hasResponseData={hasResponseData}>
					<Typography typoColor={hasResponseData ? "neutral_00_100" : "neutral_80_32"} typoSize="Button2">
						{_T("대답하기")}
					</Typography>
				</S.Button>
				<S.Button hasResponseData onClick={close}>
					<Typography typoColor="neutral_00_100" typoSize="Button2">
						{_T("닫기")}
					</Typography>
				</S.Button>
			</Row>
		</S.Container>
	);
}
